import type { AlmightyReferenceKind } from './almightyReference'

export type AlmightyMentionReferenceItem = {
    id: string
    kind: AlmightyReferenceKind
    url?: string
    name?: string
    duration?: number
    loading?: boolean
}

export type AlmightyMentionAsset = {
    id: string
    kind: AlmightyReferenceKind
    label: string
    previewUrl?: string
}

export type AlmightyPromptMentionToken =
    | {
          type: 'text'
          text: string
      }
    | {
          type: 'mention'
          assetId: string
          kind: AlmightyReferenceKind
          fallbackLabel: string
      }

const DEFAULT_KIND_LABEL_ZH: Record<AlmightyReferenceKind, string> = {
    image: '图片',
    video: '视频',
    audio: '音频',
}

const DEFAULT_KIND_LABEL_EN: Record<AlmightyReferenceKind, string> = {
    image: 'image',
    video: 'video',
    audio: 'audio',
}

const getDefaultKindLabel = (
    kind: AlmightyReferenceKind,
    locale?: string
) => {
    return String(locale || '').toLowerCase().startsWith('en')
        ? DEFAULT_KIND_LABEL_EN[kind]
        : DEFAULT_KIND_LABEL_ZH[kind]
}

export const getAlmightyMentionLabel = (
    kind: AlmightyReferenceKind,
    index: number,
    t?: (key: string, params?: Record<string, any>) => string,
    locale?: string
) => {
    const translated = t?.(`workbench.uploadLabels.${kind}`)
    const baseLabel =
        translated && translated !== `workbench.uploadLabels.${kind}`
            ? translated
            : getDefaultKindLabel(kind, locale)
    return `${baseLabel}${index}`
}

export const buildAlmightyMentionAssets = (
    items: AlmightyMentionReferenceItem[],
    t?: (key: string, params?: Record<string, any>) => string,
    locale?: string
): AlmightyMentionAsset[] => {
    const counters: Record<AlmightyReferenceKind, number> = {
        image: 0,
        video: 0,
        audio: 0,
    }

    return (items || [])
        .filter(
            item =>
                item &&
                typeof item.id === 'string' &&
                ['image', 'video', 'audio'].includes(item.kind) &&
                typeof item.url === 'string' &&
                !!item.url &&
                !item.loading
        )
        .map(item => {
            counters[item.kind] += 1
            return {
                id: item.id,
                kind: item.kind,
                label: getAlmightyMentionLabel(
                    item.kind,
                    counters[item.kind],
                    t,
                    locale
                ),
                previewUrl: item.kind === 'audio' ? undefined : item.url,
            }
        })
}

const mergeAdjacentTextTokens = (tokens: AlmightyPromptMentionToken[]) => {
    const merged: AlmightyPromptMentionToken[] = []

    for (const token of tokens) {
        if (token.type === 'text') {
            if (!token.text) continue
            const prev = merged[merged.length - 1]
            if (prev?.type === 'text') {
                prev.text += token.text
            } else {
                merged.push({ ...token })
            }
            continue
        }
        if (!token.assetId || !token.fallbackLabel) continue
        merged.push({ ...token })
    }

    return merged
}

export const normalizeAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    fallbackPrompt = ''
) => {
    const normalized = Array.isArray(tokens)
        ? mergeAdjacentTextTokens(
              tokens
                  .filter(
                      (token): token is AlmightyPromptMentionToken =>
                          !!token &&
                          (token.type === 'text' || token.type === 'mention')
                  )
                  .map(token => {
                      if (token.type === 'text') {
                          return {
                              type: 'text' as const,
                              text: typeof token.text === 'string' ? token.text : '',
                          }
                      }

                      return {
                          type: 'mention' as const,
                          assetId:
                              typeof token.assetId === 'string'
                                  ? token.assetId
                                  : '',
                          kind: token.kind,
                          fallbackLabel:
                              typeof token.fallbackLabel === 'string'
                                  ? token.fallbackLabel
                                  : '',
                      }
                  })
          )
        : []

    if (normalized.length > 0) return normalized
    if (fallbackPrompt) return [{ type: 'text' as const, text: fallbackPrompt }]
    return []
}

export const cloneAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    fallbackPrompt = ''
) => {
    return normalizeAlmightyPromptMentionTokens(tokens, fallbackPrompt).map(token =>
        token.type === 'text'
            ? { type: 'text' as const, text: token.text }
            : {
                  type: 'mention' as const,
                  assetId: token.assetId,
                  kind: token.kind,
                  fallbackLabel: token.fallbackLabel,
              }
    )
}

export const pruneAlmightyPromptMentionTokensByAssetIds = (
    tokens?: AlmightyPromptMentionToken[] | null,
    validAssetIds?: Iterable<string>
) => {
    const validIds = new Set(validAssetIds || [])
    return cloneAlmightyPromptMentionTokens(
        normalizeAlmightyPromptMentionTokens(tokens).filter(
            token => token.type === 'text' || validIds.has(token.assetId)
        )
    )
}

export const serializeAlmightyPromptMentionTokens = (
    tokens?: AlmightyPromptMentionToken[] | null,
    assets: AlmightyMentionAsset[] = []
) => {
    const assetLabelMap = new Map(assets.map(asset => [asset.id, asset.label]))

    return normalizeAlmightyPromptMentionTokens(tokens)
        .map(token => {
            if (token.type === 'text') return token.text
            return assetLabelMap.get(token.assetId) || token.fallbackLabel
        })
        .join('')
}

export const createAlmightyPromptTextTokens = (prompt = '') => {
    return prompt ? [{ type: 'text' as const, text: prompt }] : []
}
